import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Avatar } from '@mui/material';
import { Celebration } from '@mui/icons-material';
import { getAuthenticatedAvatarUrl } from 'Shared/Utils/Domain/Services/AvatarUrlService';

const HomeBirthdayCard = ({ birthdays = [] }) => {
  // Only display birthdays that are today; do not show upcoming birthdays
  const list = (birthdays || []).filter((b) => b && b.isToday !== false);

  if (list.length === 0) {
    return null;
  }

  const isMultiple = list.length > 1;
  const isTwo = list.length === 2;
  const single = list[0];
  const singleAvatarUrl = single.avatar ? getAuthenticatedAvatarUrl(single.avatar) : null;
  const avatarSize = isTwo ? 64 : (list.length <= 4 ? 54 : 46);

  return (
    <Box
      sx={{
        borderRadius: '18px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        bgcolor: '#ffffff',
        p: { xs: 2.5, sm: 3 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Title */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Celebration sx={{ color: '#257eb9', fontSize: '1.25rem' }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            color: '#1f7bb6',
            fontSize: '1.05rem',
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}
        >
          ¡FELIZ CUMPLEAÑOS!
        </Typography>
      </Box>

      {/* Content: Single Celebrant vs Mosaic */}
      {!isMultiple ? (
        <Box
          sx={{
            my: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 1,
            width: '100%',
          }}
        >
          {/* Decorative avatar with sparkles/stars */}
          <Box sx={{ position: 'relative', mb: 2, display: 'inline-flex' }}>
            <Box
              sx={{
                position: 'absolute',
                top: -12,
                left: -18,
                right: -18,
                bottom: -12,
                pointerEvents: 'none',
                zIndex: 0,
              }}
            >
              <svg viewBox="0 0 140 140" width="100%" height="100%" fill="none">
                {/* Left sparkles */}
                <path d="M 15 45 Q 18 35 25 35 Q 18 35 15 25 Q 12 35 5 35 Q 12 35 15 45 Z" fill="#38bdf8" />
                <circle cx="12" cy="65" r="3" fill="#60a5fa" />
                <path d="M 20 85 Q 26 80 20 75" stroke="#38bdf8" strokeWidth="2" strokeLinecap="round" />
                {/* Right sparkles */}
                <path d="M 125 45 Q 128 35 135 35 Q 128 35 125 25 Q 122 35 115 35 Q 122 35 125 45 Z" fill="#38bdf8" />
                <circle cx="128" cy="65" r="3" fill="#38bdf8" />
                <path d="M 120 85 Q 114 80 120 75" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" />
                {/* Party hat line */}
                <path d="M 28 20 L 40 8 L 48 24 Z" stroke="#257eb9" strokeWidth="2" fill="#dbeafe" />
              </svg>
            </Box>

            <Avatar
              src={singleAvatarUrl || undefined}
              alt={single.fullName}
              sx={{
                width: 96,
                height: 96,
                bgcolor: '#257eb9',
                fontSize: '2.4rem',
                fontWeight: 700,
                boxShadow: '0 8px 24px rgba(37, 126, 185, 0.25)',
                border: '4px solid #ffffff',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {single.fullName ? single.fullName.charAt(0).toUpperCase() : '🎂'}
            </Avatar>
          </Box>

          {/* Single Person details */}
          <Typography
            sx={{
              color: '#1f7bb6',
              fontSize: '0.98rem',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {single.fullName}
          </Typography>
          <Typography
            sx={{
              color: '#64748b',
              fontSize: '0.82rem',
              fontWeight: 500,
              mt: 0.3,
            }}
          >
            {single.department || single.jobPosition || 'Aszende'}
          </Typography>
        </Box>
      ) : (
        /* Mosaic Grid for Multiple Celebrants */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isTwo ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
            gap: 1.5,
            width: '100%',
            my: 'auto',
            py: 1,
            maxHeight: { xs: '280px', md: '300px' },
            overflowY: list.length > 4 ? 'auto' : 'visible',
            pr: list.length > 4 ? 0.5 : 0,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: '4px' },
          }}
        >
          {list.map((person, idx) => {
            const authAvatar = person.avatar ? getAuthenticatedAvatarUrl(person.avatar) : null;
            return (
              <Box
                key={person.id || idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#f0f9ff',
                    borderColor: '#93c5fd',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(37, 126, 185, 0.12)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', mb: 1 }}>
                  <Avatar
                    src={authAvatar || undefined}
                    alt={person.fullName}
                    sx={{
                      width: avatarSize,
                      height: avatarSize,
                      bgcolor: '#257eb9',
                      fontSize: isTwo ? '1.5rem' : '1.2rem',
                      fontWeight: 700,
                      border: '2px solid #ffffff',
                      boxShadow: '0 4px 12px rgba(37, 126, 185, 0.2)',
                    }}
                  >
                    {person.fullName ? person.fullName.charAt(0).toUpperCase() : '🎂'}
                  </Avatar>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      bgcolor: '#ffffff',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontSize: '0.7rem',
                    }}
                  >
                    🎉
                  </Box>
                </Box>

                <Typography
                  sx={{
                    color: '#1f7bb6',
                    fontSize: isTwo ? '0.85rem' : '0.78rem',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.2,
                    lineHeight: 1.2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  {person.fullName}
                </Typography>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    mt: 0.3,
                    lineHeight: 1.2,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  {person.department || person.jobPosition || 'Aszende'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Static reminder banner */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: '#257eb9',
          color: 'white',
          fontWeight: 700,
          fontSize: '0.82rem',
          borderRadius: '10px',
          py: 1.2,
          px: 2,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          boxShadow: '0 4px 12px rgba(37, 126, 185, 0.25)',
          userSelect: 'none',
          mt: 2,
        }}
      >
        <Celebration fontSize="small" sx={{ fontSize: '1.1rem' }} />
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.82rem',
            letterSpacing: 0.5,
            color: 'inherit',
          }}
        >
          {isMultiple ? '¡NO OLVIDES FELICITARLES!' : '¡NO OLVIDES FELICITARL@!'}
        </Typography>
      </Box>
    </Box>
  );
};

HomeBirthdayCard.propTypes = {
  birthdays: PropTypes.array,
};

export default HomeBirthdayCard;
